import Axios from "axios";
import { Magic } from "magic-sdk";
import store from "..";
import config from "../../utils/config";
import { SET_BLOCKED, SET_USER, SIGN_OUT } from "../actions/actionTypes";
import { cleanCache } from "../../utils";
import { refreshTokens } from "./auth.handlers";

// Setup base URL prefix based on environment
const prefix = process.env.NODE_ENV === "qa" ? "v1/" : "";
const client = Axios.create({ baseURL: `${config.app.apiUrl}/${prefix}payout` });

let isRefreshing = false;
let failedQueue = [];

// Centralized logout function
const logoutUser = () => {
  const magic = new Magic(config.PK_MAGIC_LINK);
  magic.user.logout();
  store.dispatch({ type: SIGN_OUT });
};

// Retry queued requests after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
  failedQueue = [];
};

// Response Interceptor
client.interceptors.response.use(
  (response) => {
    const version = response?.headers["x-api-version"];
    const localVersion = localStorage.getItem("version");
    if (version && version !== localVersion) {
      cleanCache(version);
      localStorage.setItem("version", version);
    }
    store.dispatch({ type: SET_BLOCKED, payload: false });
    return response.data;
  },
  (error) => {
    const originalRequest = error.config;

    if (error.code === "ERR_NETWORK") return logoutUser();

    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes("refresh")) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => failedQueue.push({ resolve, reject }))
          .then(token => { originalRequest.headers["Authorization"] = `Bearer ${token}`; return client(originalRequest); });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return refreshTokens()
        .then((data) => {
          const newToken = data?.result?.access;
          store.dispatch({ type: SET_USER, payload: data?.result });
          client.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return client(originalRequest);
        })
        .catch((err) => {
          processQueue(err);
          logoutUser();
        })
        .finally(() => { isRefreshing = false; });
    }

    if (error.response?.status === 403 && !window.location.href.includes("login")) {
      store.dispatch({ type: SET_BLOCKED, payload: true });
      logoutUser();
      setTimeout(() => store.dispatch({ type: SIGN_OUT }), 50000);
    }

    return Promise.reject(error);
  }
);

// Request Interceptor
client.interceptors.request.use((config) => {
  const state = store.getState();
  const accountId = state?.Auth?.account?.payoutAccount?.id;
  if (!accountId && (config.url?.includes('/bank-detail') ||
    config.url.includes('/getDashboardData') || config.url.includes('/compliance-status') ||  config.url.includes('/services'))) {
    // Throwing a cancel error to prevent the request
    return Promise.reject(new Axios.Cancel('account-id-error-cancel'));
  }

  if (!config.headers['x-api-account'] && accountId) {
    const encodedBuffer = btoa(accountId, 'utf-8');
    config.headers['x-api-account'] = encodedBuffer;
  }

  const token = store.getState()?.Auth?.tokens?.[config?.url?.includes('refresh') ? 'refresh' : 'access'];
  if (token) config.headers.Authorization = `Bearer ${token}`;

  return config;
});

export default client;
